import React, { useState, useRef, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { USER_ROLES } from "../data/models";
import logo from "../assets/logo.png";

interface HeaderProps {
  theme: "light" | "dark";
  setTheme: (theme: "light" | "dark") => void;
  logout: () => void;
}

export default function Header({ theme, setTheme }: HeaderProps) {
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const { currentUser, logout } = useAuth();
  const profileRef = useRef<HTMLDivElement>(null);

  // Get user initial for avatar
  const getUserInitial = () => {
    if (!currentUser || !currentUser.name) return "U";
    return currentUser.name.charAt(0).toUpperCase();
  };

  // Get role-based background color
  const getRoleBgColor = () => {
    switch (currentUser?.role) {
      case USER_ROLES.CLIENT:
        return "bg-gray-600";
      case USER_ROLES.DATA_COLLECTOR:
        return "bg-gray-600";
      case USER_ROLES.SUPERVISOR:
        return "bg-gray-600";
      default:
        return "bg-gray-600";
    }
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        profileRef.current &&
        !profileRef.current.contains(event.target as Node)
      ) {
        setIsProfileOpen(false);
      }
    };

    if (isProfileOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isProfileOpen]);

  return (
    <header className="bg-white shadow-sm">
      <div className="flex justify-between items-center px-6 py-3">
        <div className="flex items-center">
          <img src={logo} alt="Value Logo" className="h-16 w-16" />
          <span className="text-sm text-gray-500 ml-2">
            Real-Time Property Valuation
          </span>
        </div>

        {currentUser && (
          <div className="flex items-center space-x-4">
            <span className="hidden md:block text-sm text-gray-600">
              {currentUser.name}
              {currentUser.role === USER_ROLES.CLIENT && (
                <span className="ml-1 text-xs text-gray-500">
                  ({currentUser.upi})
                </span>
              )}
            </span>
          </div>
        )}

        <div className="ml-3 relative" ref={profileRef}>
          <div>
            <button
              onClick={() => setIsProfileOpen(!isProfileOpen)}
              className="flex items-center cursor-pointer text-sm rounded-md bg-gray-800/10 text-gray-800 px-3 py-2 hover:bg-gray-800/20"
            >
              <span className="mr-2">👤</span>
              {currentUser?.role}
              <span className="ml-1">▼</span>
            </button>
          </div>

          {isProfileOpen && (
            <div
              className={`origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg ${
                theme === "dark" ? "bg-gray-500" : "bg-white"
              } ring-1 ring-black ring-opacity-5 py-1 z-50`}
            >
              <a
                href="#"
                className={`block px-4 py-2 text-sm ${
                  theme === "dark"
                    ? "hover:bg-gray-500 text-white"
                    : "hover:bg-gray-100 text-gray-900"
                }`}
              >
                Profile Settings
              </a>
        
              <a
                href="#"
                className={`block px-4 py-2 text-sm ${
                  theme === "dark"
                    ? "hover:bg-gray-500 text-white"
                    : "hover:bg-gray-100 text-gray-900"
                }`}
              >
                Account Security
              </a>
              <a
                href="#"
                className={`block px-4 py-2 text-sm ${
                  theme === "dark"
                    ? "hover:bg-gray-600 text-white"
                    : "hover:bg-gray-100 text-gray-900"
                }`}
              >
                Preferences
              </a>
              <div className="border-t border-gray-200 dark:border-gray-600 my-1"></div>
              <span
                onClick={() => {
                  logout();
                  window.location.href = "/login";
                }}
                className={`block px-4 py-2 text-sm text-red-600 ${
                  theme === "dark" ? "hover:bg-gray-600" : "hover:bg-gray-100"
                }`}
              >
                Logout
              </span>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
